/**
 * MultiSelectDropdown Component
 * Reusable multi-select filter dropdown with search
 * Swiss design aesthetic matching the existing FilterPanel
 */

import React, { useState, useRef, useEffect } from 'react';
import { BRAND_COLORS, TYPOGRAPHY } from '../types/data';
import { ChevronDown, Check, X, Search } from 'lucide-react';

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectDropdownProps {
  label: string;
  options: MultiSelectOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  searchable?: boolean;
  maxHeight?: number;
}

export const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  label,
  options,
  selectedValues,
  onChange,
  placeholder = 'Select options...',
  disabled = false,
  searchable = true,
  maxHeight = 300,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Filter options based on search query
  const filteredOptions = searchable && searchQuery
    ? options.filter(option =>
        option.label.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : options;

  const toggleOption = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const clearAll = () => {
    onChange([]);
  };

  const selectAll = () => {
    onChange(filteredOptions.map(opt => opt.value));
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      {/* Dropdown Trigger Button */}
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        aria-label={`${label} filter`}
        aria-expanded={isOpen}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 12px',
          fontSize: TYPOGRAPHY.sizes.sm,
          fontWeight: TYPOGRAPHY.weights.medium,
          fontFamily: TYPOGRAPHY.fontFamily,
          border: `1px solid ${isOpen ? BRAND_COLORS.primary : BRAND_COLORS.border}`,
          borderRadius: '6px',
          backgroundColor: selectedValues.length > 0 ? BRAND_COLORS.surface : BRAND_COLORS.background,
          color: BRAND_COLORS.text,
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, overflow: 'hidden' }}>
          <span
            style={{
              fontSize: TYPOGRAPHY.sizes.sm,
              fontWeight: TYPOGRAPHY.weights.semibold,
              color: BRAND_COLORS.textSecondary,
            }}
          >
            {label}
          </span>
          {selectedValues.length > 0 && (
            <span
              style={{
                backgroundColor: BRAND_COLORS.primary,
                color: '#FFFFFF',
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: TYPOGRAPHY.sizes.xs,
                fontWeight: TYPOGRAPHY.weights.bold,
              }}
            >
              {selectedValues.length}
            </span>
          )}
        </div>
        <ChevronDown
          size={16}
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
            color: BRAND_COLORS.textSecondary,
          }}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            backgroundColor: BRAND_COLORS.background,
            border: `1px solid ${BRAND_COLORS.border}`,
            borderRadius: '6px',
            boxShadow: BRAND_COLORS.shadows.lg,
            zIndex: 1000,
            overflow: 'hidden',
          }}
        >
          {/* Search Input */}
          {searchable && (
            <div
              style={{
                padding: '12px',
                borderBottom: `1px solid ${BRAND_COLORS.border}`,
              }}
            >
              <div
                style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Search
                  size={16}
                  style={{
                    position: 'absolute',
                    left: '10px',
                    color: BRAND_COLORS.textSecondary,
                  }}
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search..."
                  style={{
                    width: '100%',
                    padding: '8px 12px 8px 36px',
                    fontSize: TYPOGRAPHY.sizes.sm,
                    fontFamily: TYPOGRAPHY.fontFamily,
                    border: `1px solid ${BRAND_COLORS.border}`,
                    borderRadius: '4px',
                    backgroundColor: BRAND_COLORS.surface,
                    color: BRAND_COLORS.text,
                    outline: 'none',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = BRAND_COLORS.primary;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = BRAND_COLORS.border;
                  }}
                />
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '8px 12px',
              borderBottom: `1px solid ${BRAND_COLORS.border}`,
              backgroundColor: BRAND_COLORS.surface,
            }}
          >
            <button
              onClick={selectAll}
              style={{
                padding: '4px 8px',
                fontSize: TYPOGRAPHY.sizes.xs,
                fontWeight: TYPOGRAPHY.weights.medium,
                fontFamily: TYPOGRAPHY.fontFamily,
                border: 'none',
                borderRadius: '4px',
                backgroundColor: 'transparent',
                color: BRAND_COLORS.primary,
                cursor: 'pointer',
                transition: 'background-color 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = BRAND_COLORS.surface;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              Select All
            </button>
            {selectedValues.length > 0 && (
              <button
                onClick={clearAll}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 8px',
                  fontSize: TYPOGRAPHY.sizes.xs,
                  fontWeight: TYPOGRAPHY.weights.medium,
                  fontFamily: TYPOGRAPHY.fontFamily,
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: 'transparent',
                  color: BRAND_COLORS.textSecondary,
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = BRAND_COLORS.surface;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <X size={12} />
                Clear
              </button>
            )}
          </div>

          {/* Options List */}
          <div
            style={{
              maxHeight: `${maxHeight}px`,
              overflowY: 'auto',
              padding: '4px',
            }}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const isSelected = selectedValues.includes(option.value);
                return (
                  <button
                    key={option.value}
                    onClick={() => toggleOption(option.value)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 12px',
                      fontSize: TYPOGRAPHY.sizes.sm,
                      fontWeight: isSelected ? TYPOGRAPHY.weights.semibold : TYPOGRAPHY.weights.regular,
                      fontFamily: TYPOGRAPHY.fontFamily,
                      border: 'none',
                      borderRadius: '4px',
                      backgroundColor: isSelected ? BRAND_COLORS.surface : 'transparent',
                      color: isSelected ? BRAND_COLORS.primary : BRAND_COLORS.text,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = BRAND_COLORS.surface;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <span>{option.label}</span>
                    {isSelected && (
                      <Check
                        size={16}
                        style={{
                          color: BRAND_COLORS.primary,
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </button>
                );
              })
            ) : (
              <div
                style={{
                  padding: '16px',
                  textAlign: 'center',
                  fontSize: TYPOGRAPHY.sizes.sm,
                  color: BRAND_COLORS.textMuted,
                }}
              >
                No options found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
